import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, FileUploadZone, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { useModals } from '@domain-group/react-hooks'

import useImageLibrary, { useImageLibraryForm } from '@hooks/useImageLibrary'
import useUppyImageUpload from '@hooks/useUppyImageUpload'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { confirmDelete } from '@functions/entity'

import EntitySelector from '@components/EntitySelector'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import type { ActionResult } from '@redux/modules/types'

import type { ImageLibraryModel } from '@models/types'

import ImageList from './components/ImageList'

const MODAL_KEY = 'CreateOrEditImageLibraryModal'

type ModalCallbacks = {
  closeModal: () => void,
  createImageLibrary: (payload: HandleCallbackActionParams) => Promise<object>,
  deleteImageLibrary: (payload: HandleCallbackActionParams) => Promise<object>,
  updateImageLibrary: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrUpdateImageLibraryParams = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<ImageLibraryModel>,
  setModalData: ReturnType<typeof useModals>['callbacks']['setModalData'],
  updateFn: Function,
}

const createOrUpdateImageLibrary = (params: CreateOrUpdateImageLibraryParams) => {
  const {
    callbacks, createFn, entityState, setModalData, updateFn,
  } = params

  const { closeModal, createImageLibrary, updateImageLibrary } = callbacks || {}

  const text = entityState.id ? 'Updated' : 'Added'
  const actionFn = entityState.id ? updateFn : createFn

  const afterAction = entityState.id
    ? closeModal
    : ({ response }: { response: ActionResult<ImageLibraryModel> }) => (
      // @ts-expect-error TS2339
      setModalData('CreateOrEditImageLibraryModal', { imageLibrary: response.data })
    )

  const imageLibraryPayload = {
    callbacks: {
      action: actionFn,
      afterAction,
    },
    entityParams: entityState,
    requestOptions: {
      include_team_image_library_id: true,
      include_image_library_cover_image: true,
      include_image_library_image_count: true,
      include_image_library_owner: true,
      include_image_sizes: true,
    },
    toastText: `Image Library ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updateImageLibrary(imageLibraryPayload)

  // @ts-expect-error TS2345
  return createImageLibrary(imageLibraryPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditImageLibraryModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const {
    callbacks: {
      setModalData,
    },
    modalData,
  } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    imageLibrary: initImageLibrary,
  } = modalPayload

  const imageLibraryPayload = useImageLibrary(initImageLibrary)
  const {
    imageLibrary,
    callbacks: {
      createImageLibrary: createFn,
      deleteImageLibrary: deleteFn,
      updateImageLibrary: updateFn,
    },
    creating,
    deleting,
    editable: isEditable,
    updating,
  } = imageLibraryPayload

  const imageLibraryFormPayload = useImageLibraryForm(imageLibrary, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      id,
      owner_id,
      owner_type,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = imageLibraryFormPayload

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      action: deleteFn,
    },
    strings: { entityName: 'Image Library' },
  }
  const updateOrCreateButtonText = id ? 'Save Image Library' : 'Create Image Library'

  const image_type = 'Image'
  const imageable_type = 'ImageLibrary'

  const uppy = useUppyImageUpload(
    {
      callbacks: {
        afterAction: () => {},
      },
      image_type,
      imageable_id: id,
      imageable_type,
      requestOptions: {
        include_image_sizes: true,
      },
    },
    [image_type, id, imageable_type],
  )

  const editable = !id || isEditable

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit' : 'Add'}
        titleSecondLine="Image Library"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title">
            <input disabled={!editable} name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            <Form.Field label="* Owner Type" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                disabled={!!id}
                name="owner_type"
                value={owner_type}
                {...handlers}
              >
                <option value="">Please Select...</option>
                <option value="Agency">Agency</option>
                <option value="Brand">Brand</option>
              </select>
            </Form.Field>

            {owner_type === 'Agency' && (
              <EntitySelector
                callbacks={{
                  selectItem: agency => setEntityState({ owner_id: agency ? agency.id : null }),
                }}
                disabled={!!id}
                entityKey="agencies"
                errorMessage={errors.owner_id}
                label="Agency"
                marginTop="large"
                selectedItemId={owner_id}
              />
            )}

            {owner_type === 'Brand' && (
              <EntitySelector
                callbacks={{
                  selectItem: brand => setEntityState({ owner_id: brand ? brand.id : null }),
                }}
                disabled={!!id}
                entityKey="brands"
                errorMessage={errors.owner_id}
                label="Brand"
                marginTop="large"
                selectedItemId={owner_id}
              />
            )}
          </ScopedConditionalContent>

          {id && editable && (
            <Form.Field label="Upload Image" marginTop="large">
              <FileUploadZone uppy={uppy} />
            </Form.Field>
          )}

          {id && <ImageList imageableId={id} imageableType="ImageLibrary" />}

          {id && editable && owner_type !== 'Team' && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                // @ts-expect-error TS2345
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="medium"
              >
                Delete Image Library
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        {editable && (
          <Button
            buttonStyle="primaryCreate"
            disabled={!saveEnabled || !editable}
            loading={creating || updating}
            onClick={() => createOrUpdateImageLibrary({
              callbacks, createFn, entityState, setModalData, updateFn,
            })}
            size="large"
          >
            {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
          </Button>
        )}

        {!editable && (
          <Button
            buttonStyle="secondaryUtility"
            onClick={() => closeModal()}
            size="large"
          >
            Close Modal
          </Button>
        )}
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditImageLibraryModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
